import {useState} from 'react';
import styled from 'styled-components';
import {EmptyState} from './EmptyState';
import {graphql, useFragment} from 'react-relay';
import {Button, EMPHASIS} from '@forecast-it/design-system';
import type {TimeTab_ticket$key} from './__generated__/TimeTab_ticket.graphql';
import type {ticketingPerson_person$key} from '../../../graphql/fragments/__generated__/ticketingPerson_person.graphql';
import {ticketingPersonFragment, personDisplayName} from '../../../graphql/fragments/ticketingPerson';
import {formatDateFull} from '../utils/formatDate';
import {LogTimeModal, type LogTimeModalTask} from './LogTimeModal';

// `ticketTimeRegistrations` is the time logged THROUGH this ticket. Walking
// `linkedTasks { timeRegistrations }` instead would pull in every registration on
// a linked task — including time booked from Forecast's own timesheet or via
// another ticket — which is what inflated the table and the stat cards.
//
// `linkedTasks` is still selected, but only for the Log time modal's task picker.
const TimeTabFragment = graphql`
	fragment TimeTab_ticket on Ticket {
		id
		linkedTasks {
			id
			name
			project {
				id
			}
		}
		ticketTimeRegistrations {
			id
			minutesRegistered
			billableMinutesRegistered
			notes
			date
			day
			month
			year
			task {
				name
			}
			person {
				...ticketingPerson_person
			}
			role {
				name
			}
		}
	}
`;

interface Props {
	ticket: TimeTab_ticket$key;
	currentPersonId: string;
	onRefetch?: () => void;
}

interface Row {
	id: string;
	taskName: string;
	person: ticketingPerson_person$key | null;
	roleName: string | null;
	date: string | null;
	sortKey: number;
	note: string | null;
	minutes: number;
	billableMinutes: number;
}

const Stack = styled.div`
	> * + * {
		margin-top: 24px;
	}
`;
const HeaderRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
`;
const Title = styled.h3`
	margin: 0;
	${p => p.theme.sys.font?.subheading?.['1']}
	letter-spacing: 0.15px;
	color: #0f172a;
`;
const StatGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 16px;
`;
const StatCard = styled.div`
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	padding: 16px;
`;
const StatLabel = styled.p`
	margin: 0;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.025em;
	color: #64748b;
`;
const StatValue = styled.p`
	margin: 4px 0 0;
	font-size: 24px;
	line-height: 32px;
	font-weight: 600;
	color: #0f172a;
`;
const EmptyCard = styled(EmptyState)`
	border-style: dashed;
	background: ${p => p.theme.sys.color.background.accent['1']};
`;
const EmptyText = styled.p`
	margin: 0;
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #475569;
`;
const TableCard = styled.div`
	overflow: hidden;
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	box-shadow: 0 1px 2px rgb(0 0 0 / 0.05);
`;
const Table = styled.table`
	min-width: 100%;
	border-collapse: collapse;
`;
const Thead = styled.thead`
	background: #f8fafc;
`;
const Th = styled.th<{$right?: boolean}>`
	padding: 10px 16px;
	text-align: ${p => (p.$right ? 'right' : 'left')};
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.025em;
	color: #64748b;
	border-bottom: 1px solid #e2e8f0;
`;
const Tr = styled.tr`
	border-top: 1px solid #f1f5f9;
	&:hover {
		background: #f8fafc;
	}
`;
const Td = styled.td<{$nowrap?: boolean; $right?: boolean; $truncate?: boolean}>`
	padding: 10px 16px;
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #475569;
	${p => p.$nowrap && 'white-space: nowrap;'}
	${p => p.$right && 'text-align: right;'}
	${p => p.$truncate && 'max-width: 20rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;'}
`;
const StrongTd = styled(Td)`
	color: #0f172a;
`;
const HoursTd = styled(Td)`
	font-weight: 500;
	color: #0f172a;
`;
const BillableNote = styled.span`
	margin-left: 4px;
	${p => p.theme.sys.font?.detail?.['1']}
	color: #64748b;
`;

/** Format minutes as a one-decimal hour value, e.g. 90 → "1.5h". */
function hoursLabel(minutes: number): string {
	return `${(minutes / 60).toFixed(1)}h`;
}

function PersonCell({person}: {person: ticketingPerson_person$key | null}) {
	const data = useFragment(ticketingPersonFragment, person);
	return <>{personDisplayName(data)}</>;
}

export function TimeTab({ticket: ticketRef, currentPersonId, onRefetch}: Props) {
	const data = useFragment(TimeTabFragment, ticketRef);
	const [logOpen, setLogOpen] = useState(false);

	const tasks: LogTimeModalTask[] = data.linkedTasks.map(t => ({
		id: t.id,
		name: t.name ?? 'Untitled task',
		projectId: t.project?.id ?? null,
	}));

	// Each registration carries its own task, so the Task column no longer needs
	// the linkedTasks walk.
	const rows: Row[] = data.ticketTimeRegistrations.map(reg => ({
		id: reg.id,
		taskName: reg.task?.name ?? 'Untitled task',
		person: reg.person ?? null,
		roleName: reg.role?.name ?? null,
		date: reg.date ?? null,
		sortKey: (reg.year ?? 0) * 10000 + (reg.month ?? 0) * 100 + (reg.day ?? 0),
		note: reg.notes ?? null,
		minutes: reg.minutesRegistered ?? 0,
		billableMinutes: reg.billableMinutesRegistered ?? 0,
	}));
	rows.sort((a, b) => b.sortKey - a.sortKey);

	const totalMinutes = rows.reduce((sum, r) => sum + r.minutes, 0);
	const totalBillable = rows.reduce((sum, r) => sum + r.billableMinutes, 0);

	return (
		<Stack>
			<HeaderRow>
				<Title>Time</Title>
				{tasks.length > 0 && (
					<Button emphasis={EMPHASIS.HIGH} onPress={() => setLogOpen(true)}>
						+ Log time
					</Button>
				)}
			</HeaderRow>

			<StatGrid>
				<StatCard>
					<StatLabel>Total logged</StatLabel>
					<StatValue>{hoursLabel(totalMinutes)}</StatValue>
				</StatCard>
				<StatCard>
					<StatLabel>Billable</StatLabel>
					<StatValue>{hoursLabel(totalBillable)}</StatValue>
				</StatCard>
				<StatCard>
					<StatLabel>Entries</StatLabel>
					<StatValue>{rows.length}</StatValue>
				</StatCard>
			</StatGrid>

			{rows.length === 0 ? (
				<EmptyCard>
					<EmptyText>No time logged yet.</EmptyText>
				</EmptyCard>
			) : (
				<TableCard>
					<Table>
						<Thead>
							<tr>
								<Th>Task</Th>
								<Th>Person</Th>
								<Th>Role</Th>
								<Th>Date</Th>
								<Th>Note</Th>
								<Th $right>Hours</Th>
							</tr>
						</Thead>
						<tbody>
							{rows.map(row => (
								<Tr key={row.id}>
									<StrongTd $nowrap>{row.taskName}</StrongTd>
									<Td $nowrap>
										<PersonCell person={row.person} />
									</Td>
									<Td $nowrap>{row.roleName ?? '—'}</Td>
									<Td $nowrap>{row.date ? formatDateFull(row.date) : '—'}</Td>
									<Td $truncate>{row.note ?? '—'}</Td>
									<HoursTd $nowrap $right>
										{(row.minutes / 60).toFixed(1)}
										{row.billableMinutes !== row.minutes && (
											<BillableNote>({(row.billableMinutes / 60).toFixed(1)} billable)</BillableNote>
										)}
									</HoursTd>
								</Tr>
							))}
						</tbody>
					</Table>
				</TableCard>
			)}

			{logOpen && (
				<LogTimeModal
					open
					personId={currentPersonId}
					ticketId={data.id}
					tasks={tasks}
					onClose={() => setLogOpen(false)}
					onSuccess={() => {
						setLogOpen(false);
						onRefetch?.();
					}}
				/>
			)}
		</Stack>
	);
}
